import { StatusMatchingConfig, DynamicStatuses, DynamicStatusColors, StatusPrecedence } from '@/types/statusBar.types';

export const COPYRIGHT_TEXT :string= "Copyright ";
export const CURRENT_YEAR :number= new Date().getFullYear();
export const TJ_TEXT :string= " By Trader Joe's";
// constants.ts
export const VALID_ROLES = [
    "CATEGORY_MANAGER",
    "PRODUCT_CREATOR",
    "SUPER_ADMIN"
  ] as const;

export const ROLEMAP: Record<string, string> = {
    CATEGORY_MANAGER: "Category Manager",
    PRODUCT_CREATOR: "Product Creator",
    SUPER_ADMIN: "Super Admin"
};

export const VA_FORM_SECTIONS: string[] = ["vendorAssessmentForm", "requiredDocumentsForm", "ingredientsDetailsForm", "vendorContactInformationForm", "manufacturingFacilityInfoForm"];

export const VENDOR_INFORMATION_SAVE_PRESERVE_EMPTY_IDENTITY_FIELDS: string[] = [
  "vendorCorporateName",
  "vendorFirstName",
  "vendorLastName",
  "vendorEmailAddress",
];

/**
 * Optional vendor fields: send explicit "" when cleared (no country/id guard required).
 */
export const VENDOR_INFORMATION_SAVE_PRESERVE_EMPTY_CLEARABLE_FIELDS: string[] = [
  "tariff",
  "vendorCountry",
];

/**
 * Vendor Information save: preserve empty strings for these keys so API receives explicit clears.
 */
export const VENDOR_INFORMATION_SAVE_PRESERVE_EMPTY_STRING_FIELDS: string[] = [
  ...VENDOR_INFORMATION_SAVE_PRESERVE_EMPTY_IDENTITY_FIELDS,
  "vendorCorporateAddress",
  "vendorCity",
  "vendorState",
  "vendorZipCode",
  ...VENDOR_INFORMATION_SAVE_PRESERVE_EMPTY_CLEARABLE_FIELDS,
];

/**
 * Product Information save: keep explicit "" for these keys so updateIdea clears them.
 * formatIdea otherwise drops empty values and the API keeps prior values.
 * BE only clears when the value is "" (null is ignored).
 */
export const PRODUCT_INFORMATION_SAVE_PRESERVE_EMPTY_CLEARABLE_FIELDS: string[] = [
  "businessCategory",
  "categoryManagerUserId",
  "productType",
  "sellingPeriod",
  "requiredAttributes",
  "businessCategoryIdsConcatenated",
  "productSpecificationType",
];

/**
 * Pre Panel / Business Review product-analysis selects: preserve null on save.
 * Business Review reuses the Pre Panel save pipeline.
 */
export const PRE_PANEL_SAVE_PRESERVE_EMPTY_CLEARABLE_FIELDS: string[] = [
  ...PRODUCT_INFORMATION_SAVE_PRESERVE_EMPTY_CLEARABLE_FIELDS,
  "categoryManagerUser",
  "countryOfOrigin",
  "typeOfPackage",
  "retailStructure",
  "logistics",
  "typeOfVendor",
  "panelType",
  "amountOfProductsInGiftPack",
];

/**
 * Tasting Panel selects across General / Package / Retail sections.
 */
export const TASTING_PANEL_SAVE_PRESERVE_EMPTY_CLEARABLE_FIELDS: string[] = [
  "businessCategory",
  "productType",
  "sellingPeriod",
  "countryOfOrigin",
  "vendorCountry",
  "panelType",
  "categoryManagerUserId",
  "categoryManagerUser",
  "developer",
  "label",
  "requiredAttributes",
  "logistics",
  "retailStructure",
];

/**
 * Vendor Assessment questionnaire + Vendor Information select fields.
 */
export const VENDOR_ASSESSMENT_SAVE_PRESERVE_EMPTY_CLEARABLE_FIELDS: string[] = [
  "productDeveloper",
  "typeOfVendor",
  "currentlyManufactureSameSimilarProduct",
  "canTrackLotCodes",
  "prop65Warning",
  "privateLabelFosUsCompanies",
  "outsourceProduction",
  "sameTypeOfProductUnderConsideration",
  "facilityGfsiCertified",
  "certificationWithdrawnWithinLast5Years",
  "haccpFspPlan",
  "testFinishedProductsForPathogens",
  "metalDetectionXrayOnProductLine",
  "allergenManagementProgram",
  "productRecallProcedure",
  "recallWithinLast5Years",
  "facilityFdaUsdaInspected",
  "regulatoryEnforcementAction",
  "adulteratedProductPoliciesProcedures",
  "importAutomaticDetention",
  "factor",
  "childLaborVendor",
  "animalWelfareFraud",
  "fsma",
  "countryOfOrigin",
  "edi",
  "logistics",
  "retailStructure",
];

export type ValidRole = "CATEGORY_MANAGER" | "PRODUCT_CREATOR" | "SUPER_ADMIN";




export const HOLD_CONFIRM_TITLE:string = "Place This Product On Hold";
export const HOLD_CONFIRM_MSG:string = "Once you have confirmed, this product idea will be on hold.";
export const CANCEL_CONFIRM_TITLE:string = "Place This Product On Cancel";
export const CANCEL_CONFIRM_MSG:string = "Once you have confirmed, this product idea will be canceled.";
export const DELETE_VENDOR_IDEA_PROMPT_TITLE:string = "Delete Idea Related to Vendor";
export const DELETE_VENDOR_IDEA_PROMPT:string = "Do you want to delete the idea related to the vendor?";
export const DELETE_VENDOR_GENERAL_PROMPT_TITLE:string = "Cancel The Vendor";
export const DELETE_VENDOR_GENERAL_PROMPT:string = "Once you have confirmed, this vendor will be canceled.";


// ERROR MESSAGES
export const IDEA_CANCELLATION_ERROR_MESSAGE:string = "An error occured, Idea can't be canceled.";
export const IDEA_HOLD_ERROR_MESSAGE:string = "An error occurred, Idea can't hold.";
export const IDEA_SAVE_ERROR_MESSAGE :string = "An error occured, Idea can't be saved.";
export const IDEA_DELETE_ERROR_MESSAGE :string = "An error occured, Idea can't be deleted.";
export const IDEA_REACTIVATION_ERROR_MESSAGE  :string = "An error occured, Idea can't be reactivated.";
export const VENDOR_INFO_FORM_REQUIRED_FIELDS_WARNING :string = "Before submitting for review make sure you complete all * required fields in Vendor Information form";
export const PRODUCT_INFO_FORM_REQUIRED_FIELDS_WARNING  :string = "Before submitting for review make sure you complete all * required fields in Product Information form";
export const IDEA_SHARE_FOR_REVIEW_ERROR_MESSAGE   :string = "An error occured, Idea can't be shared for review.";
export const REVIEW_COMPLETION_ERROR_MESSAGE:string = "An error occured, Review can't be completed.";
export const EMAIL_FORMAT_ERROR_MESSAGE :string = "Please check the email format.";
export const SAVE_BEFORE_SUBMIT_ERROR_MESSAGE :string = "Please save the form before submitting.";
export const TRY_AGAIN_MESSAGE :string = "Please try again.";
export const CONNECTION_LOST_MESSAGE:string = "Connection lost. Changes will be synced when the connection is restored.";
//  SUCCESS MESSAGES
export const ITEM_REACTIVATED_SUCCESS_MESSAGE :string = "This item has been reactivated on the shopping list";
export const PRODUCT_SUBMISSION_SUCCESS_MESSAGE :string = "Product was successfully submitted for review";

//WARNING/INFO MESSAGES
export const SAVE_IN_PROGRESS_INFO:string = 'Wait. Saving in progress';
export const VA_INPUT_MAX_LENGTH = 120;
export const PRODUCT_NAME_MAX_LENGTH = 50;
/** Max length for vendor corporate name and vendor name fields (aligned with `constants.json`). */
export const VENDOR_CORPORATE_OR_VENDOR_NAME_MAX_LENGTH = 35;

export const VA_GUEST_TASK_COMPLETED_MSG:string = "The Vendor Assessment form has been successfully submitted for review.";
export const CONNECTION_OFFLINE_MESSAGE:string = "Offline mode: Your changes will be saved locally"
export const CONNECTION_ONLINE_MESSAGE:string = "Connection restored."

export const DEFAULT_FILE_SIZE = 40; // 40mb

export const vendorListUrl = "/create-product/vendor-assessment";
export const businessReviewListUrl = "/panel/business-review-panel";
export const tastingPanelListUrl = "/panel/tasting-panel";

// Status Bar Configuration Constants
export const STATUS_BAR_CONFIG: StatusMatchingConfig = {
  // Enable/disable status normalization
  enableStatusNormalization: true,
  
  // Enable/disable partial matching
  enablePartialMatching: true,
  
  // Default colors for different step states
  colors: {
    active: '#C5F09A',
    inactive: '#F3F4F6',
    warning: '#FFFEE2',
    error: '#FFD9D9'
  }
} as const;

// Dynamic status replacement constants
export const DYNAMIC_STATUSES: DynamicStatuses = {
  NEED_MORE_INFORMATION: 'need_more_information',
  NEEDS_REVISION: 'needs_revision',
  ON_HOLD: 'on_hold',
  ARCHIVED: 'archived',
  NOT_PROGRESSED: 'not_progressed',
} as const;

// Dynamic status colors
export const DYNAMIC_STATUS_COLORS: DynamicStatusColors = {
  [DYNAMIC_STATUSES.NEED_MORE_INFORMATION]: '#FFFEE2', // Light yellow
  [DYNAMIC_STATUSES.NEEDS_REVISION]: '#FFFEE2',        // Light yellow
  [DYNAMIC_STATUSES.ON_HOLD]: '#89EAFF',              // Light blue
  [DYNAMIC_STATUSES.ARCHIVED]: '#FFD0D3',              // Light red
  [DYNAMIC_STATUSES.NOT_PROGRESSED]: '#E7D4D4',        // Light pink
} as const;

// Status precedence configuration for determining which status to show
// Higher numbers indicate higher precedence
export const STATUS_PRECEDENCE: StatusPrecedence = {
  'superseded': 20,
  'discontinued': 21,
  'to_be_discontinued': 22,
  'ready_for_orders': 19,
  'prepare_for_launch': 19,
  'ready_for_tasting_panel': 5,
  'tasting_panel_approved': 6,
  'testing_panel_approved': 6, // Handle both spellings
  'vendor_approved': 4,
  'in_progress': 3,
  'not_started': 2,
  'on_hold': 1,
  'archived': 0
} as const;
// Base64 encoded TJ Logo
export const BASE64_TJ_LOGO = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxnIGNsaXAtcGF0aD0idXJsKCNjbGlwMF81Mjc1XzI0NzM1KSI+DQo8cGF0aCBkPSJNMjAgNDBDOC45NTYyNSA0MCAwIDMxLjA0MzggMCAyMEMwIDguOTU2MjUgOC45NTYyNSAwIDIwIDBDMzEuMDQzOCAwIDQwIDguOTU2MjUgNDAgMjBDNDAgMzEuMDQzOCAzMS4wNDM4IDQwIDIwIDQwWiIgZmlsbD0iI0M3MjEyNyIvPg0KPHBhdGggZD0iTTIyLjg0MjIgMTcuMDQ5OEgzNC4wNDIyVjE3LjY2MjNDMzEuMzIzNCAxOS4wODExIDMwLjE0MjIgMjAuNjE4NiAzMC4xNDIyIDI0LjM0MzZDMzAuMTQyMiAyOC4wNjg2IDI5LjcyMzQgMzQuMzE4NiAyMy44NzM0IDM0LjMxODZDMTkuNjE3MiAzNC4zMTg2IDE4LjcyOTcgMjguOTQ5OCAxNS41OTg0IDI5LjI0MzZMMTUuMzA0NyAyOC44ODczTDIyLjg2NzIgMjQuOTI0OEwyMy4wNDIyIDI1LjMzNzNDMjEuMDI5NyAyNy4yODczIDIyLjY4NTkgMzAuMzE4NiAyMy43NDg0IDMwLjMyNDhDMjYuMTY3MiAzMC4zNDM2IDI2LjcwNDcgMjcuMzg3MyAyNi43MDQ3IDI0LjA5OThDMjYuNzA0NyAyMC44MTIzIDI0LjkwNDcgMTguODM3MyAyMi44MzU5IDE3LjUzNzNWMTcuMDQ5OEgyMi44NDIyWiIgZmlsbD0id2hpdGUiLz4NCjxwYXRoIGQ9Ik0xNy45MzkxIDIzLjM4MTRDMTYuMjgyOCAyMC40NjI3IDE1LjY3MDMgMTYuMjEyNyAxNS42NzAzIDEzLjIxODlDMTUuNjcwMyAxMC4yMjUyIDE2LjQzOTEgMTAuMzgxNCAxNy4xNzAzIDEwLjM4MTRDMTcuOTAxNiAxMC4zODE0IDE5LjMzOTEgMTAuNzgxNCAyMC4zNDUzIDEyLjc5MzlMMjAuODc2NiAxMi4zODE0TDE4LjU1NzggNi4wNDM5NUg5LjIwMTU2TDYuODgyODEgMTIuMzc1Mkw3LjQxNDA2IDEyLjc4NzdDOC40MjAzMSAxMC43NzUyIDkuODY0MDYgMTAuMzc1MiAxMC41ODkxIDEwLjM3NTJDMTEuMzE0MSAxMC4zNzUyIDEyLjA4OTEgMTAuMjE4OSAxMi4wODkxIDEzLjIxMjdDMTIuMDg5MSAxNi4yMDY0IDExLjQ3NjYgMjAuNDYyNyA5LjgyMDMxIDIzLjM3NTJIMTcuOTM5MVYyMy4zODE0WiIgZmlsbD0id2hpdGUiLz4NCjwvZz4NCjxkZWZzPg0KPGNsaXBQYXRoIGlkPSJjbGlwMF81Mjc1XzI0NzM1Ij4NCjxyZWN0IHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgZmlsbD0id2hpdGUiLz4NCjwvY2xpcFBhdGg+DQo8L2RlZnM+DQo8L3N2Zz4NCg==";
